import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Lenis from "@studio-freight/lenis";

import ThemeProvider from "@/components/theme-provider";
import SiteLayout from "@/components/site/SiteLayout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Projects from "@/pages/Projects";
import Culture from "@/pages/Culture";
import Career from "@/pages/Career";
import CareerDetail from "@/pages/CareerDetail";
import JobApplication from "@/pages/JobApplication";
import Pricing from "@/pages/Pricing";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";

// Workshop pages & layouts
import Workshops from "@/pages/Workshops";
import WorkshopDetail from "@/pages/WorkshopDetail";
import WorkshopRegister from "@/pages/WorkshopRegister";
import ProjectDetail from "@/pages/ProjectDetail";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLayout from "@/components/admin/AdminLayout";
import ProtectedAdminRoute from "@/components/admin/ProtectedAdminRoute";
import AdminWorkshops from "@/pages/admin/AdminWorkshops";
import AdminWorkshopEditor from "@/pages/admin/AdminWorkshopEditor";
import AdminWorkshopRegistrations from "@/pages/admin/AdminWorkshopRegistrations";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminProjectEditor from "@/pages/admin/AdminProjectEditor";
import AdminTaxonomy from "@/pages/admin/AdminTaxonomy";
import AdminCareers from "@/pages/admin/AdminCareers";
import AdminCareerEditor from "@/pages/admin/AdminCareerEditor";
import AdminJobApplications from "@/pages/admin/AdminJobApplications";

// Service pages

import WebDevelopment from "@/pages/services/WebDevelopment";
import MobileDevelopment from "@/pages/services/MobileDevelopment";
import ERPServices from "@/pages/services/ERPServices";
import SaaSDevelopment from "@/pages/services/SaaSDevelopment";
import UIUXDesign from "@/pages/services/UIUXDesign";
import CloudDevOps from "@/pages/services/CloudDevOps";
import MaintenanceSupport from "@/pages/services/MaintenanceSupport";
import MvpRapidPrototyping from "@/pages/services/MvpRapidPrototyping";
import AiMlStrategy from "@/pages/services/AiMlStrategy";
import GenerativeAiSolutions from "@/pages/services/GenerativeAiSolutions";

import { Toaster } from "@/components/ui/sonner";
import TawkToWidget from "@/components/TawkToWidget";

function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

function LenisRoot({ children }) {
  useEffect(() => {
    if (isMobileDevice()) {
      // Remove all Lenis classes from html/body on mobile
      const html = document.documentElement;
      const body = document.body;
      html.classList.remove('lenis', 'lenis-smooth', 'lenis-stopped');
      body.classList.remove('lenis', 'lenis-smooth', 'lenis-stopped');
      // Force native scroll CSS on mobile
      html.style.scrollBehavior = 'auto';
      body.style.scrollBehavior = 'auto';
      html.style.overscrollBehavior = 'auto';
      body.style.overscrollBehavior = 'auto';
      return;
    }
    const lenis = new Lenis({
      duration: 1.15,
      smoothWheel: true,
      syncTouch: true,
      wheelMultiplier: 0.95,
    });

    let rafId = 0;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return children;
}

function App() {
  return (
    <ThemeProvider>
      <LenisRoot>
        <BrowserRouter>
          <TawkToWidget />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/web-development" element={<WebDevelopment />} />
              <Route path="/services/mobile-development" element={<MobileDevelopment />} />
              <Route path="/services/erp-services" element={<ERPServices />} />
              <Route path="/services/saas-development" element={<SaaSDevelopment />} />
              <Route path="/services/uiux-design" element={<UIUXDesign />} />
              <Route path="/services/cloud-devops" element={<CloudDevOps />} />
              <Route path="/services/maintenance-support" element={<MaintenanceSupport />} />

                {/* New Service Pages */}
                <Route path="/services/mvp-rapid-prototyping" element={<MvpRapidPrototyping />} />
                <Route path="/services/ai-ml-strategy" element={<AiMlStrategy />} />
                <Route path="/services/generative-ai-solutions" element={<GenerativeAiSolutions />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:slug" element={<ProjectDetail />} />
              <Route path="/culture" element={<Culture />} />
              <Route path="/career" element={<Career />} />
              <Route path="/careers/:slug" element={<CareerDetail />} />
              <Route path="/apply" element={<JobApplication />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Legal />} />
              <Route path="/terms" element={<Legal />} />

              {/* Public Workshop Routes */}
              <Route path="/workshops" element={<Workshops />} />
              <Route path="/workshops/:slug" element={<WorkshopDetail />} />
              <Route path="/workshops/:slug/register" element={<WorkshopRegister />} />
            </Route>

            {/* Standalone Admin Login (No SiteLayout, No TawkTo) */}
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Authenticated Admin Area (Protected by ProtectedAdminRoute) */}
            <Route element={<ProtectedAdminRoute />}>
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/projects" element={<AdminProjects />} />
                <Route path="/admin/projects/new" element={<AdminProjectEditor />} />
                <Route path="/admin/projects/:slug/edit" element={<AdminProjectEditor />} />
                <Route path="/admin/projects/:id/edit" element={<AdminProjectEditor />} />
                <Route path="/admin/taxonomy" element={<AdminTaxonomy />} />
                <Route path="/admin/workshops" element={<AdminWorkshops />} />
                <Route path="/admin/workshops/new" element={<AdminWorkshopEditor />} />
                <Route path="/admin/workshops/:slug/edit" element={<AdminWorkshopEditor />} />
                <Route path="/admin/workshops/:id/edit" element={<AdminWorkshopEditor />} />
                <Route path="/admin/workshops/:slug/registrations" element={<AdminWorkshopRegistrations />} />
                <Route path="/admin/workshops/:id/registrations" element={<AdminWorkshopRegistrations />} />
                {/* Career Management Routes */}
                <Route path="/admin/careers" element={<AdminCareers />} />
                <Route path="/admin/careers/new" element={<AdminCareerEditor />} />
                <Route path="/admin/careers/:id/edit" element={<AdminCareerEditor />} />
                <Route path="/admin/careers/applications" element={<AdminJobApplications />} />
                <Route path="/admin/careers/:id/applications" element={<AdminJobApplications />} />
              </Route>
            </Route>

            {/* Fallback 404 Route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </LenisRoot>
    </ThemeProvider>
  );
}

export default App;
